import { some, sortBy } from 'lodash-es';
import type { EmailServiceDocument } from 'models/emailService/EmailServiceDocument';
import { FC, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { removeElements } from 'state/utils/array';

import { EmptyState } from '../EmptyState/EmptyState';
import { DocumentListItem } from './DocumentListItem/DocumentListItem';

export interface DocumentsSelectorProps {
  e2e?: string;
  id?: string;
  onChange: (value: readonly EmailServiceDocument[]) => void;
  value: readonly EmailServiceDocument[] | undefined;
  options: readonly EmailServiceDocument[];
}

// TODO: UNit tests
export const DocumentsSelector: FC<DocumentsSelectorProps> = ({ e2e, id, onChange, value = [], options }) => {
  const hasDocuments = useMemo(() => options.length > 0, [options]);
  const isSelected = useCallback((option: EmailServiceDocument) => some(value, option), [value]);
  const onSelectDocument = useCallback(
    (option: EmailServiceDocument) => {
      const newValue = !isSelected(option) ? [...value, option] : removeElements(value, option);
      onChange(newValue);
    },
    [value, onChange, isSelected],
  );

  const documents = useMemo(
    () =>
      hasDocuments ? (
        <>
          {sortBy(options, 'type', 'language').map(opt => {
            return (
              <DocumentListItem
                {...opt}
                key={`${opt.type}-${opt.language}`}
                isSelected={isSelected(opt)}
                onSelected={opt => onSelectDocument(opt)}
              />
            );
          })}
        </>
      ) : (
        <EmptyState hideTitle icon="insert_drive_file" prefix="emailService.documents" className="text-secondary" />
      ),
    [hasDocuments, options, isSelected, onSelectDocument],
  );

  return (
    <div className="form-group">
      <label htmlFor="email-service-document" className="form-label mb-1">
        <FormattedMessage id="emailService.fields.attachDocument" />
        <span className="ps-1 text-danger">*</span>
      </label>
      <div id={id} data-e2e={e2e} className="documents-selector-component">
        <ul className="list-group list-group-flush border" data-e2e={`${e2e}-documents-list`}>
          {documents}
        </ul>
      </div>
    </div>
  );
};

DocumentsSelector.displayName = 'DocumentsSelector';
